import { motion } from 'framer-motion';
import { Search, Plus, MessageSquare, SlidersHorizontal } from 'lucide-react';
import type { Conversation } from '@/types/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ConversationItem } from './ConversationItem';
import { EmptyConversationsState } from './EmptyStates';
import { WhatsAppIcon, MessengerIcon, InstagramIcon, WebsiteIcon, ShopifyIcon } from './BrandIcons';
import { LIST_CONTAINER } from '../lib/motion';

interface ConversationListProps {
  conversations: Conversation[];
  selectedConversation: string | null;
  onSelect: (id: string) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  platformFilter: string;
  onPlatformFilterChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
}

const CHANNEL_PILLS: Array<{
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  activeBg: string;
  activeBorder: string;
  iconColor?: string;
}> = [
  {
    id: 'all',
    label: 'All',
    activeBg: 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900',
    activeBorder: 'border-slate-900 dark:border-slate-100',
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    icon: WhatsAppIcon,
    iconColor: 'text-[#25D366]',
    activeBg: 'bg-[#25D366] text-white',
    activeBorder: 'border-[#25D366]',
  },
  {
    id: 'facebook',
    label: 'Messenger',
    icon: MessengerIcon,
    iconColor: 'text-[#0084FF]',
    activeBg: 'bg-[#0084FF] text-white',
    activeBorder: 'border-[#0084FF]',
  },
  {
    id: 'instagram',
    label: 'Instagram',
    icon: InstagramIcon,
    iconColor: 'text-[#E4405F]',
    activeBg: 'bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white',
    activeBorder: 'border-pink-500',
  },
  {
    id: 'shopify',
    label: 'Shopify',
    icon: ShopifyIcon,
    iconColor: 'text-[#96BF48]',
    activeBg: 'bg-[#96BF48] text-white',
    activeBorder: 'border-[#96BF48]',
  },
  {
    id: 'website',
    label: 'Website',
    icon: WebsiteIcon,
    iconColor: 'text-indigo-500',
    activeBg: 'bg-indigo-600 text-white',
    activeBorder: 'border-indigo-600',
  },
];

export const ConversationList = ({
  conversations,
  selectedConversation,
  onSelect,
  searchTerm,
  onSearchChange,
  platformFilter,
  onPlatformFilterChange,
  statusFilter,
  onStatusFilterChange,
}: ConversationListProps) => {
  const filterKey = `${searchTerm}|${platformFilter}|${statusFilter}`;

  const unreadCount = conversations.reduce((acc, c) => acc + (c.unreadCount || 0), 0);

  return (
    <div className="flex h-full w-[340px] shrink-0 flex-col border-r border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900">
      {/* ServerlessLens Pod Header */}
      <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/70 bg-slate-100/50 dark:bg-slate-900/80 px-3.5 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 dark:bg-primary/20 text-primary">
            <MessageSquare className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
            Inbox
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/80 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            {conversations.length} Active
          </span>
        </div>
        {unreadCount > 0 && (
          <span className="inline-flex items-center rounded-full bg-primary px-2 py-0.2 text-[10px] font-bold text-white shadow-2xs">
            {unreadCount} unread
          </span>
        )}
      </div>

      {/* Search & Filter Toolbar */}
      <div className="space-y-2 border-b border-slate-200/80 dark:border-slate-800 p-3 bg-slate-50/40 dark:bg-slate-950/40">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <Input
            placeholder="Search messages or customers..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="h-8.5 rounded-full border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 pl-9 text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-primary/40 focus:ring-primary/20 shadow-2xs"
          />
        </div>

        {/* Channel Quick Filter Pills */}
        <div className="flex items-center gap-2">
          {CHANNEL_PILLS.map((pill) => {
            const isActive = platformFilter === pill.id;
            const Icon = pill.icon;
            return (
              <button
                key={pill.id}
                type="button"
                title={pill.label}
                aria-label={pill.label}
                onClick={() => onPlatformFilterChange(pill.id)}
                className={cn(
                  'h-8 w-8 rounded-full flex items-center justify-center transition-all duration-200 shrink-0 border',
                  isActive
                    ? cn(pill.activeBg, pill.activeBorder, 'shadow-xs scale-105 ring-2 ring-primary/20')
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200/80 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-750 shadow-2xs hover:scale-105'
                )}
              >
                {Icon ? (
                  <Icon
                    className={cn(
                      'h-4 w-4 transition-transform duration-150',
                      isActive ? 'text-white' : pill.iconColor
                    )}
                  />
                ) : (
                  <span className="text-[11px] font-bold">All</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Status Filter Row */}
        <div className="flex items-center justify-between pt-0.5">
          <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            <SlidersHorizontal className="h-3 w-3" />
            <span>Status:</span>
          </div>
          <div className="flex gap-1">
            {[
              { id: 'all', label: 'All' },
              { id: 'active', label: 'Active' },
              { id: 'human', label: 'Needs Human' },
              { id: 'resolved', label: 'Resolved' },
            ].map((st) => {
              const isSelected = statusFilter === st.id;
              return (
                <button
                  key={st.id}
                  type="button"
                  onClick={() => onStatusFilterChange(st.id)}
                  className={cn(
                    'rounded-md px-1.5 py-0.5 text-[10px] font-medium transition-colors',
                    isSelected
                      ? 'bg-slate-200/80 dark:bg-slate-800 font-bold text-slate-900 dark:text-slate-100'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                  )}
                >
                  {st.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-2">
        {conversations.length === 0 ? (
          <EmptyConversationsState />
        ) : (
          <motion.div
            key={filterKey}
            variants={LIST_CONTAINER}
            initial="hidden"
            animate="show"
            className="space-y-1.5"
          >
            {conversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                isSelected={selectedConversation === conversation.id}
                onClick={() => onSelect(conversation.id)}
              />
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};
